/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/

/**
 * ServerEvents originate from the server and are sent to the client
 */
export enum ServerEvents {
  ClusterCreated = 'cluster_created',
  ClusterUpdated = 'cluster_updated',
  ClusterDeleted = 'cluster_deleted',

  LogicalBackupCompleted = 'logical_backup_completed',
  LogicalBackupFailed = 'logical_backup_failed',

  OrganizationInvitation = 'organization_invitation',
  RemovedFromOrganization = 'removed_from_organization',

  AddedToProject = 'added_to_project',
  RemovedFromProject = 'removed_from_project',

  ImportStarted = 'import_started',
  ImportSuccessful = 'import_successful',
  ImportAborted = 'import_aborted',
  ImportFailedInsufficientSpace = 'import_failed_insufficient_space',
  ImportFailedSchemaExists = 'import_failed_schema_exists',
  ImportFailedDatabaseNotFound = 'import_failed_database_not_found',
  ImportFailedInvalidCredentials = 'import_failed_invalid_credentials',
  ImportFailed = 'import_failed',
}

/**
 * Helper type to access the interface that a websocket message should have
 */
export declare type ServerEvent<EventType extends ServerEvents> = {
  type: EventType;

  requestIdentifier: string;
  payload: ServerEventInterfaces[EventType];
};

/**
 * The possible cluster states
 */
export enum ClusterStates {
  Creating = 'Creating',
  Updating = 'Updating',
  Importing = 'Importing',
  Running = 'Running',
  Stopped = 'Stopped',
  Stopping = 'Stopping',
  Starting = 'Starting',
  Unavailable = 'Unavailable',
  Error = 'Error',
}

/**
 * organizationId should never be undefined
 */
class ClusterCreated {
  organizationId!: string;
  projectId!: string;
  clusterId!: string;
  status!: ClusterStates;
  oldStatus!: ClusterStates;
}

class LogicalBackupCompleted {
  organizationId!: string;
  projectId!: string;
  clusterId!: string;
}

class OrganizationInvitationOrganization {
  id!: string;
  name!: string;
  companyName!: string;
}

class OrganizationInvitationUser {
  id!: string;
  firstName!: string;
  lastName!: string;
}

class OrganizationInvitation {
  invitationId!: string;
  organization!: OrganizationInvitationOrganization;
  user!: OrganizationInvitationUser;
}

class RemovedFromOrganization {
  organization!: OrganizationInvitationOrganization;
  user!: OrganizationInvitationUser;
}

class AddedToProjectOrganization {
  id!: string;
  name!: string;
}

class AddedToProjectProject {
  id!: string;
  name!: string;
}

class AddedToProjectUser {
  id!: string;
  firstName!: string;
  lastName!: string;
}

class AddedToProject {
  organization!: AddedToProjectOrganization;
  project!: AddedToProjectProject;
  user!: AddedToProjectUser;
}

class DatasetImport {
  organizationId!: string;
  projectId!: string;
  clusterId!: string;
  datasetName!: string;
}

/**
 * Mappings of the events to their respective interfaces
 */
export interface ServerEventInterfaces {
  [ServerEvents.ClusterCreated]: ClusterCreated;
  [ServerEvents.ClusterUpdated]: ClusterCreated;
  [ServerEvents.ClusterDeleted]: ClusterCreated;

  [ServerEvents.LogicalBackupCompleted]: LogicalBackupCompleted;
  [ServerEvents.LogicalBackupFailed]: LogicalBackupCompleted;

  [ServerEvents.OrganizationInvitation]: OrganizationInvitation;
  [ServerEvents.RemovedFromOrganization]: RemovedFromOrganization;

  [ServerEvents.AddedToProject]: AddedToProject;
  [ServerEvents.RemovedFromProject]: AddedToProject;

  [ServerEvents.ImportStarted]: DatasetImport;
  [ServerEvents.ImportSuccessful]: DatasetImport;
  [ServerEvents.ImportAborted]: DatasetImport;
  [ServerEvents.ImportFailedInsufficientSpace]: DatasetImport;
  [ServerEvents.ImportFailedSchemaExists]: DatasetImport;
  [ServerEvents.ImportFailedDatabaseNotFound]: DatasetImport;
  [ServerEvents.ImportFailedInvalidCredentials]: DatasetImport;
  [ServerEvents.ImportFailed]: DatasetImport;
}

/**
 * Mappings of the events to their respective validator classes
 */
export const ServerEventClasses: ServerEventClassesMapping = {
  [ServerEvents.ClusterCreated]: ClusterCreated,
  [ServerEvents.ClusterUpdated]: ClusterCreated,
  [ServerEvents.ClusterDeleted]: ClusterCreated,

  [ServerEvents.LogicalBackupCompleted]: LogicalBackupCompleted,
  [ServerEvents.LogicalBackupFailed]: LogicalBackupCompleted,

  [ServerEvents.OrganizationInvitation]: OrganizationInvitation,
  [ServerEvents.RemovedFromOrganization]: OrganizationInvitation,

  [ServerEvents.AddedToProject]: AddedToProject,
  [ServerEvents.RemovedFromProject]: AddedToProject,

  [ServerEvents.ImportStarted]: DatasetImport,
  [ServerEvents.ImportSuccessful]: DatasetImport,
  [ServerEvents.ImportAborted]: DatasetImport,
  [ServerEvents.ImportFailedInsufficientSpace]: DatasetImport,
  [ServerEvents.ImportFailedSchemaExists]: DatasetImport,
  [ServerEvents.ImportFailedDatabaseNotFound]: DatasetImport,
  [ServerEvents.ImportFailedInvalidCredentials]: DatasetImport,
  [ServerEvents.ImportFailed]: DatasetImport,
};

/**
 * Helper type for mapping the validator classes
 */
type ServerEventClassesMapping = {
  [ServerEvent in ServerEvents]: new (...args: any[]) => ServerEventInterfaces[ServerEvent];
};

/**
 * The possible lifecycle states for a cluster
 */
export enum ClusterLifecycleStates {
  Stopping = 0,
  Starting = 1,
}
